// ./src/components/maps/MapContainer.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Map, Marker, ZoomControl, Overlay } from "pigeon-maps";
import axios from "axios";
import { navigate } from "gatsby";

// Mapbox Access and Style URL
const GATSBY_MAPBOX_ACCESS_TOKEN = process.env.GATSBY_MAPBOX_ACCESS_TOKEN || "";
const GATSBY_MAPBOX_URL = "lececil123/cm1quujef00gt01pl4616glxy";



// Define the shape of `mapState`
export interface MapState {
  center: [number, number];
  zoom: number;
}

// Define the props for `MapContainer`
export interface MapContainerProps {
  apiBaseUrl: string;
  mapState: MapState;
  setMapState: React.Dispatch<React.SetStateAction<MapState>>;
  selectedLocation?: {
    lat: number;
    lng: number;
  };
  resetToDefault?: boolean;
}

// Map tile provider for Mapbox
const mapTiler = (x: number, y: number, z: number, dpr: number): string => {
  return `https://api.mapbox.com/styles/v1/${GATSBY_MAPBOX_URL}/tiles/512/${z}/${x}/${y}${dpr >= 2 ? "@2x" : ""
    }?access_token=${GATSBY_MAPBOX_ACCESS_TOKEN}`;
};

const MapContainer: React.FC<MapContainerProps> = ({
  mapState,
  setMapState,
  selectedLocation,
  resetToDefault = false,
}) => {
  const { center, zoom } = mapState;

  const [posts, setPosts] = useState<any[]>([]);
const [points, setPoints] = useState<any[]>([]);
const [events, setEvents] = useState<any[]>([]);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredMarker, setHoveredMarker] = useState<{
    id: string;
    title: string;
    location: { latitude: string; longitude: string };
    status: string;
  } | null>(null);

  const apiBaseUrl = process.env.GATSBY_API_URL;

  // Fetch user location from localStorage
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      try {
        setUserLocation(JSON.parse(storedLocation));
      } catch (err) {
        console.error("Error parsing user location from localStorage", err);
      }
    }
    setLoading(false);
  }, []);

  // Reset map state if resetToDefault is true
  useEffect(() => {
    if (resetToDefault) {
      setMapState({ center: [50.879, 2.6997], zoom: 3 });
    }
  }, [resetToDefault, setMapState]);

  // Update map state when a new location is selected
  useEffect(() => {
    if (selectedLocation?.lat && selectedLocation?.lng) {
      setMapState({ center: [selectedLocation.lat, selectedLocation.lng], zoom: 10 });
    }
  }, [selectedLocation, setMapState]);

  // Fetch posts using REST API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found in localStorage");
        return;
      }

      try {
        const [postsResponse, pointsResponse, eventsResponse] = await Promise.all([
          axios.get(`${apiBaseUrl}/wp-json/wp/v2/posts?per_page=100`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiBaseUrl}/wp-json/wp/v2/point?context=edit&status=draft,publish,pending,private&per_page=50`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiBaseUrl}/wp-json/wp/v2/event?per_page=50`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
  
        console.log("📌 Fetched Posts:", postsResponse.data);
        console.log("📍 Fetched Points:", pointsResponse.data);
        console.log("🎉 Fetched Events:", eventsResponse.data);
  
        setPosts(postsResponse.data);
        setPoints(pointsResponse.data);
        setEvents(eventsResponse.data);
      } catch (err) {
        console.error("Error fetching posts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiBaseUrl]);

  // Handle marker click to navigate
  const handleMarkerClick = useCallback(
    (lat: number, lng: number, uri: string) => {
      setMapState({ center: [lat, lng], zoom: 10 });
      setTimeout(() => navigate(uri), 300);
    },
    [setMapState]
  );

  // Handle marker hover events
  const handleMarkerMouseOver = useCallback((node: any) => {
    setHoveredMarker(node);
  }, []);

  const handleMarkerMouseOut = useCallback(() => {
    setHoveredMarker(null);
  }, []);

  // Render map markers
  const renderMarkers = (data, color) => {
    return data.map((item) => {
      const latitude = item.meta?.["map-latitude"];
      const longitude = item.meta?.["map-longitude"];
      const { status, id, title, link } = item;

      // Skip if location data is missing
      if (!latitude || !longitude) {
        console.warn(`Post ${id} missing location data`);
        return null;
      }

      const isPublished = status === "publish";

      return (
        <Marker
          key={id}
          anchor={[parseFloat(latitude), parseFloat(longitude)]}
          width={33}
          color={isPublished ? color : "gray"}
          onClick={
            isPublished
    ? () => {
        const cleanLink = link.replace(/^https?:\/\/dev\.map4nomads\.com/, ""); // ✅ Remove domain
        handleMarkerClick(parseFloat(latitude), parseFloat(longitude), cleanLink);
      }
    : undefined
          }
          onMouseOver={() =>
            handleMarkerMouseOver({
              id,
              title: title.rendered,
              location: { latitude, longitude },
              status,
            })
          }
          onMouseOut={handleMarkerMouseOut}
        />
      );
    });
  };

  // Handle user geolocation
  const handleUseMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: [number, number] = [latitude, longitude];
          setUserLocation(location);
          setMapState({ center: location, zoom: 10 });
          localStorage.setItem("userLocation", JSON.stringify(location));
        },
        (error) => {
          console.error("Error fetching user location", error);
        }
      );
    }
  }, [setMapState]);

  if (loading) {
    return (
      <div className="loadingContainer">
        <div className="spinner"></div>
        <span className="loadingText">Loading map...</span>
      </div>
    );
  }

  return (
    <div>
      <button
        className="location-button"
        tabIndex="-1"
        title="Get your location"
        aria-label="Get your location"
        onClick={handleUseMyLocation}
      >
        <span>My location</span>
      </button>

      <Map
        provider={mapTiler}
        dprs={[1, 2]}
        center={center}
        zoom={zoom}
        zoomSnap={false}
        animate={true}
        touchEvents={true}
        twoFingerDrag={false}
        onBoundsChanged={({ center, zoom }) => {
          setMapState({ center, zoom });
        }}
        style={{ width: "100%", minHeight: "500px" }}
      >
        {renderMarkers(posts, "black")}   {/* Posts → Black */}
        {renderMarkers(points, "blue")}   {/* Points → Blue */}
        {renderMarkers(events, "red")}    {/* Events → Red */}
        {userLocation && (
          <Marker
            anchor={userLocation}
            width={50}
            hover={false}
            color="lightblue"
          />
        )}

        {hoveredMarker && (
          <Overlay
            anchor={[
              parseFloat(hoveredMarker.location.latitude),
              parseFloat(hoveredMarker.location.longitude),
            ]}
            offset={[0, -10]}
          >
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "5px",
                borderRadius: "5px",
                maxWidth: "200px",
              }}
            >
              {hoveredMarker.title.split(":")[0]}
            </div>
          </Overlay>
        )}

        <ZoomControl />
      </Map>
    </div>
  );
};

export default MapContainer;
